/**
 * HLS Player Service
 * ------------------
 * Creates and manages an HLS.js instance (or native HLS on Safari)
 * for playing session recordings streamed from the Watch API.
 *
 * Authentication is handled by the X-API-Key header injected via
 * the xhrSetup hook. No Firebase tokens or query-string auth needed.
 *
 * Usage:
 *   const player = createHlsPlayer({ video, src, onReady, ... });
 *   // later:
 *   player.destroy();
 */

import Hls from "hls.js";
import { apiClient } from "./apiClient";

/**
 * @typedef {Object} HlsPlayerOptions
 * @property {HTMLVideoElement} video        - Target <video> element
 * @property {string}           src          - HLS playlist URL (relative to API base)
 * @property {function}         [onReady]    - Called when the manifest is parsed
 * @property {function}         [onPlay]     - Called on playback start
 * @property {function}         [onPause]    - Called on playback pause / end
 * @property {function}         [onTime]     - Called with current time in seconds
 * @property {function}         [onDuration] - Called with total duration in seconds
 * @property {function}         [onBuffered] - Called with buffered percentage (0–100)
 * @property {function}         [onResolution] - Called with (width, height)
 * @property {function}         [onBitrate]  - Called with bitrate in bps
 * @property {function}         [onError]    - Called with an error message string
 */

/**
 * Creates an HLS player instance attached to the provided video element.
 *
 * @param {HlsPlayerOptions} options
 * @returns {{ destroy: function }} Player handle
 */
export function createHlsPlayer({
  video,
  src,
  apiKey,
  onReady,
  onPlay,
  onPause,
  onTime,
  onDuration,
  onBuffered,
  onResolution,
  onBitrate,
  onError,
}) {
  if (!video) {
    throw new Error("[hlsService] video element is required");
  }

  let hls      = null;
  let destroyed = false;

  // ---------------------------------------------------------------------------
  // hls.js (Chrome, Firefox, Edge, …)
  // ---------------------------------------------------------------------------

  if (Hls.isSupported()) {
    hls = new Hls({
      enableWorker:     true,
      lowLatencyMode:   false,
      backBufferLength: 90,
      maxBufferLength:  30,
      maxMaxBufferLength: 600,

      // Inject the API key into every XHR request made by hls.js
      xhrSetup(xhr) {
        xhr.setRequestHeader("X-API-Key", apiKey ?? "");
      },
    });

    hls.loadSource(src);
    hls.attachMedia(video);

    // Manifest parsed — video metadata is available
    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      if (destroyed) return;

      const level = hls.levels?.[0];
      if (level) {
        onResolution?.(level.width, level.height);
      }

      onReady?.();
    });

    // Adaptive bitrate level switch
    hls.on(Hls.Events.LEVEL_SWITCHED, (_event, data) => {
      if (destroyed) return;
      const level = hls.levels[data.level];
      if (level) onBitrate?.(level.bitrate);
    });

    // Error handling with automatic recovery for non-fatal errors
    hls.on(Hls.Events.ERROR, (_event, data) => {
      if (destroyed) return;

      if (!data.fatal) return;

      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          console.warn("[hlsService] Fatal network error — attempting recovery");
          hls.startLoad();
          break;

        case Hls.ErrorTypes.MEDIA_ERROR:
          console.warn("[hlsService] Fatal media error — attempting recovery");
          hls.recoverMediaError();
          break;

        default:
          console.error("[hlsService] Unrecoverable error:", data.details);
          onError?.("Fatal player error — please reload the session");
          destroy();
          break;
      }
    });

  // ---------------------------------------------------------------------------
  // Native HLS (Safari / iOS)
  // ---------------------------------------------------------------------------

  } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
    console.log("[hlsService] Using native HLS engine (Safari/iOS)");
    video.src = src;
    video.addEventListener("loadedmetadata", () => onReady?.(), { once: true });

  } else {
    onError?.("HLS is not supported in this browser");
    return { destroy: () => {} };
  }

  // ---------------------------------------------------------------------------
  // Native video element events
  // ---------------------------------------------------------------------------

  function onTimeUpdate() {
    if (!destroyed) onTime?.(video.currentTime);
  }

  function onLoadedMetadata() {
    if (destroyed) return;
    onDuration?.(video.duration);
    onResolution?.(video.videoWidth, video.videoHeight);
  }

  function onProgress() {
    if (destroyed || !video.duration || video.buffered.length === 0) return;
    try {
      const end = video.buffered.end(video.buffered.length - 1);
      onBuffered?.((end / video.duration) * 100);
    } catch (_) {}
  }

  function onPlaying() { if (!destroyed) onPlay?.(); }
  function onPauseEnd() { if (!destroyed) onPause?.(); }

  video.addEventListener("loadedmetadata", onLoadedMetadata);
  video.addEventListener("timeupdate",     onTimeUpdate);
  video.addEventListener("progress",       onProgress);
  video.addEventListener("playing",        onPlaying);
  video.addEventListener("pause",          onPauseEnd);
  video.addEventListener("ended",          onPauseEnd);

  // ---------------------------------------------------------------------------
  // Cleanup
  // ---------------------------------------------------------------------------

  function destroy() {
    if (destroyed) return;
    destroyed = true;

    video.removeEventListener("loadedmetadata", onLoadedMetadata);
    video.removeEventListener("timeupdate",     onTimeUpdate);
    video.removeEventListener("progress",       onProgress);
    video.removeEventListener("playing",        onPlaying);
    video.removeEventListener("pause",          onPauseEnd);
    video.removeEventListener("ended",          onPauseEnd);

    try {
      hls?.destroy();
      video.pause();
      video.src = "";
      video.load();
    } catch (err) {
      console.warn("[hlsService] Cleanup error:", err);
    }

    hls = null;
  }

  return { destroy };
}